import { useEffect, useReducer, useRef } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    resolveActiveEnvironment,
    useActiveEditTarget,
} from '~/hooks/target/useActiveEditTarget';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import type { Environment } from '~/engine/data/Environment';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import {
    buildToolConfig,
    resolveSiblingSupports,
    type ToolArmingInputs,
} from '~/engine/tool/buildToolConfig';
import {
    type FrameRotationAccess,
    type ToolManager,
    type ToolConfig,
} from '~/engine/tool/ToolManager';
import { RegistrationMode } from '~/engine/tool/tools/RegistrationTool';
import { BrushTool } from '~/engine/tool/tools/contouring-tools/BrushTool';
import { DeformTool } from '~/engine/tool/tools/contouring-tools/DeformTool';
import { LineDeformTool } from '~/engine/tool/tools/contouring-tools/LineDeformTool';
import { SmoothBrushTool } from '~/engine/tool/tools/contouring-tools/SmoothBrushTool';
import { PanZoom } from '~/engine/view/PanZoom';
import type { EnvironmentViews } from '~/engine/view/EnvironmentViews';
import { NO_OP_VIEW_INVALIDATOR } from '~/engine/view/ViewInvalidator';
import type { ViewManager } from '~/engine/view/ViewManager';
import { bulkRoiSelectionActiveState } from '~/state/atoms/structureSet/roiAtoms';
import { resolveArmingConstraints } from '~/engine/tool/resolveDrawingConstraints';
import { useAddAnnotation } from '~/hooks/useAddAnnotation';
import { useImageStreamProgress } from '~/hooks/image/useImageStreamProgress';
import {
    measurementDraftState,
    measurementListState,
    measurementModeState,
} from '~/state/atoms/measurementAtoms';
import {
    intensityRegionDraftState,
    intensityRegionListState,
    intensityShapeState,
    lineProfileDraftState,
    lineProfileListState,
} from '~/state/atoms/intensityToolAtoms';
import { selectedAnnotationState } from '~/state/atoms/annotationListAtoms';
import {
    boundingRoiConstraintState,
    brushOptionsState,
    collisionConstraintState,
    crossSectionDrawingHistoryState,
    crossSectionShapeRequestState,
    deformOptionsState,
    intensityThresholdConstraintState,
    lineDeformOptionsState,
    lineOptionsState,
    rectangleSelectionState,
    rectangleSelectModeState,
    autoRegistrationInProgressState,
    registrationModeState,
    selectedToolState,
    smoothBrushOptionsState,
    transformAllSupportState,
    writesContours,
} from '~/state/atoms/toolsAtoms';
import { armPan } from './armPan';

/**
 * The grid resolves every {@link ToolArmingInputs} primitive itself except the
 * pan-zoom, taken from the armed environment, and the frame access, taken
 * from the manager. The registration tool arms on the fused pair's
 * Environment and views, not the edit target's.
 */
type GridToolResolution = Omit<
    ToolArmingInputs,
    'panZoom' | 'frameRotation'
> & {
    fusedEnvironment: Environment | undefined;
    fusedViews: EnvironmentViews | undefined;
    viewManager: ViewManager;
    isAutoRegistering: boolean;
};

/** The drawing history of the cross-section tool while none of its contours exist. */
const NO_CROSS_SECTIONS_DRAWN = {
    canUndo: false,
    canRedo: false,
    hasDrawing: false,
};

/**
 * The rotate tool's frame access on the grid: the frame is the pressed
 * tile's Environment's, read and written through the manager that turns
 * that Environment's views, so a drag on one tile turns its row and no
 * other. Two rows that hang one series share an Environment, and turn
 * together as they share a cursor and a window.
 */
function rowFrameRotationAccess(viewManager: ViewManager): FrameRotationAccess {
    return {
        getFrameRotation: (view) =>
            viewManager.frameRotationOf(view.environment),
        onFrameRotationChanged: (rotation, view) =>
            viewManager.setFrameRotation(view.environment, rotation),
    };
}

/**
 * The {@link ToolConfig} to arm for the grid, or undefined to fall back to pan.
 * The measurement and intensity tools paint their cursor on every grid view.
 * A write tool requires a resolved edit target (environment + structure +
 * image). Registration requires a fused pair with an unapproved
 * registration; rotation also needs a fused secondary image to turn. A tool
 * reads its environment's tiles live, so one armed before any tile mounts
 * idles until the first tile joins. pan, box-zoom, measurement and the
 * intensity tool's line and regions arm regardless: each is an annotation of
 * the primary image of the tile it is placed on, keyed to that series by
 * the tool, so none needs an edit target and each may be placed on any
 * tile that shows its primary image; the tool refuses a tile that does not
 * draw it. The free rotation arms on the row under the cursor through the
 * manager, so it needs no edit target either, only a manager to turn the
 * row through. The rectangle-select tool is a write tool: it needs the edit
 * target, and hands its drag to the consumer in the inputs.
 */
function resolveGridToolConfig(
    inputs: GridToolResolution
): ToolConfig | undefined {
    const { tool, environment, structure, image, viewManager } = inputs;
    const armingInputs: ToolArmingInputs = {
        ...inputs,
        panZoom: environment?.panZoom ?? new PanZoom(NO_OP_VIEW_INVALIDATOR),
        frameRotation: rowFrameRotationAccess(viewManager),
    };
    const hasWriteTarget = !!environment && !!structure && !!image;
    switch (tool) {
        case 'measurement':
        case 'intensity':
            return buildToolConfig({
                ...armingInputs,
                views: viewManager.everyView(),
            });
        case 'boxZoom':
        case 'rotateView':
            return buildToolConfig(armingInputs);
        case 'brush':
        case 'deform':
        case 'lineDeform':
        case 'smoothBrush':
        case 'cross-section':
        case 'rectangle-select':
        case 'transform':
            return hasWriteTarget ? buildToolConfig(armingInputs) : undefined;
        case 'line': {
            const lineMode = inputs.lineOptions.mode;
            const canDraw = lineMode === 'draw' || lineMode === 'erase';
            return hasWriteTarget && canDraw
                ? buildToolConfig(armingInputs)
                : undefined;
        }
        case 'registration': {
            const {
                fusedEnvironment,
                fusedViews,
                registrationMode,
                isAutoRegistering,
            } = inputs;
            const registration = fusedEnvironment?.registration;
            const primaryFrame =
                fusedEnvironment?.image?.parent.frameOfReferenceUid;
            // A pair shown through the inverse of a registration is not
            // edited from this side: the drag would write the matrix
            // backwards and the image would move against the cursor.
            // A running auto-registration owns the matrix until it ends.
            if (
                isAutoRegistering ||
                !fusedEnvironment ||
                !registration ||
                registration.isApproved ||
                primaryFrame === undefined ||
                !registration.editableFrom(primaryFrame)
            ) {
                return undefined;
            }
            const modeArms =
                registrationMode === RegistrationMode.TRANSLATION ||
                (registrationMode === RegistrationMode.ROTATION &&
                    fusedEnvironment.secondaryImage !== undefined);
            return modeArms
                ? buildToolConfig({
                      ...armingInputs,
                      environment: fusedEnvironment,
                      panZoom: fusedEnvironment.panZoom,
                      views: fusedViews,
                  })
                : undefined;
        }
        case 'pan':
        case 'poi-move':
            return undefined;
    }
    return assertNeverTool(tool);
}

/**
 * Exhaustiveness guard: a new {@link Tools} member without an arm above becomes
 * a compile error here rather than a tool that silently arms pan on the grid.
 */
function assertNeverTool(tool: never): undefined {
    console.warn(`Unhandled grid tool: ${String(tool)}`);
    return undefined;
}

/**
 * Writes `value` to `property` of the tool armed on `toolManager` whenever
 * `value` changes, when the armed tool is an instance of `ToolClass`; an
 * armed tool of another class is left alone.
 */
function usePushToArmedTool<T extends object, K extends keyof T>(
    toolManager: ToolManager | undefined,
    ToolClass: abstract new (...args: never[]) => T,
    property: K,
    value: T[K]
): void {
    useEffect(() => {
        const tool: unknown = toolManager?.getActiveTool();
        if (tool instanceof ToolClass) {
            tool[property] = value;
        }
    }, [toolManager, ToolClass, property, value]);
}

/**
 * Arms the production tool on the active edit target. The tool is the global
 * `selectedToolState`; the dataset, ROI and image come from
 * `useActiveEditTarget` (the active structure set resolved to its registry
 * Environment), and the tool follows that Environment's tiles through the
 * ViewManager as they mount and unmount. A write tool whose target is
 * unresolved falls back to pan so grid input never dies. A write tool gets
 * no structure while bulk selection is on or while the edit target's image
 * is incomplete, so it falls back to pan whatever armed it. Re-arms on tool
 * change, edit-target change, registry lifecycle (Environments re-mint on
 * release/re-acquire) and, for a write tool only, the image's completion: a
 * viewing tool takes no structure, so the completion leaves it and the
 * placement or drag it holds alone. The cross-section tool also re-arms on
 * each turn of the edit target's frame. Hands input back to pan on unmount.
 * The tool is armed on the interaction context of `viewManager`; nothing is
 * armed while there is none.
 */
export function useGridToolArming(
    registry: DatasetEnvironmentRegistry,
    viewManager: ViewManager | null
): void {
    const target = useActiveEditTarget();
    const fusion = useActiveFusionTarget();
    const registrationMode = useRecoilValue(registrationModeState).mode;
    const isAutoRegistering = useRecoilValue(autoRegistrationInProgressState);
    const selectedTool = useRecoilValue(selectedToolState);
    const isBulkSelectionActive = useRecoilValue(bulkRoiSelectionActiveState);
    // Only a tool that writes to contours takes a structure when armed. The
    // point move arms nothing on the grid.
    const takesStructure = writesContours(selectedTool);
    // The recorded completeness of the edit target's image, read for a write
    // tool alone, so the arm re-runs when the image completes only for a
    // tool that takes the structure then; each arm also reads the live image
    // it resolves, as a lifecycle arm runs without a render.
    const isEditImageComplete = useImageStreamProgress(
        target.environment?.image?.parent
    ).isComplete;
    const isWriteImageComplete = takesStructure && isEditImageComplete;
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);
    const [lineDeformOptions, setLineDeformOptions] = useRecoilState(
        lineDeformOptionsState
    );
    const [deformOptions, setDeformOptions] =
        useRecoilState(deformOptionsState);
    const [smoothBrushOptions, setSmoothBrushOptions] = useRecoilState(
        smoothBrushOptionsState
    );
    const lineOptions = useRecoilValue(lineOptionsState);
    const transformAllSupport = useRecoilValue(transformAllSupportState);
    const rectangleSelectMode = useRecoilValue(rectangleSelectModeState);
    const setRectangleSelection = useSetRecoilState(rectangleSelectionState);
    const setCrossSectionDrawingHistory = useSetRecoilState(
        crossSectionDrawingHistoryState
    );
    const setCrossSectionShapeRequest = useSetRecoilState(
        crossSectionShapeRequestState
    );

    // Continuously-resized values report back into their atoms; reading them
    // through refs keeps a wheel-resize from rebuilding the armed tool. The
    // brush stroke mode is read the same way: a change applies to the armed
    // brush in place, so its outline previews the new mode.
    const brushDiameterRef = useRef(brushOptions.diameter);
    brushDiameterRef.current = brushOptions.diameter;
    const brushStrokeModeRef = useRef(brushOptions.strokeMode);
    brushStrokeModeRef.current = brushOptions.strokeMode;
    const lineDeformSpreadRef = useRef(lineDeformOptions.spreadPercent);
    lineDeformSpreadRef.current = lineDeformOptions.spreadPercent;
    const deformDiameterRef = useRef(deformOptions.diameter);
    deformDiameterRef.current = deformOptions.diameter;
    const smoothBrushDiameterRef = useRef(smoothBrushOptions.diameter);
    smoothBrushDiameterRef.current = smoothBrushOptions.diameter;
    const { mode, erase } = brushOptions;

    const boundingRoiConstraint = useRecoilValue(boundingRoiConstraintState);
    const collisionConstraint = useRecoilValue(collisionConstraintState);
    const intensityThresholdConstraint = useRecoilValue(
        intensityThresholdConstraintState
    );
    const constraintsRef = useRef({
        boundingRoiConstraint,
        collisionConstraint,
        intensityThresholdConstraint,
    });
    constraintsRef.current = {
        boundingRoiConstraint,
        collisionConstraint,
        intensityThresholdConstraint,
    };
    const measurementMode = useRecoilValue(measurementModeState);
    const setMeasurementDraft = useSetRecoilState(measurementDraftState);
    const intensityShape = useRecoilValue(intensityShapeState);
    const setLineProfileDraft = useSetRecoilState(lineProfileDraftState);
    const setIntensityRegionDraft = useSetRecoilState(
        intensityRegionDraftState
    );
    const addCommitted = useAddAnnotation();
    const setSelectedIntensityAnnotation = useSetRecoilState(
        selectedAnnotationState
    );

    const structureSet = target.structureSet;
    const roiId = target.roiId;
    // Only the registration tool arms on the fused pair, so only it re-arms
    // when the primary row changes.
    const fusedSeriesUid =
        selectedTool === 'registration' ? fusion.seriesUid : undefined;
    // A frame turn re-orients the views of an Environment where they stand:
    // no view joins or leaves for a tool to follow. Only the cross-section
    // tool keeps a line and painted contours that go with the plane each of
    // its views showed, so only it re-arms when the edit target's frame
    // turns: the new tool takes over the closed contours, drops the line
    // and paints the views as they are turned.
    const frameTurnEnvironment =
        selectedTool === 'cross-section' ? target.environment : undefined;
    const [frameTurnCount, countFrameTurn] = useReducer(
        (count: number) => count + 1,
        0
    );
    useEffect(
        () =>
            viewManager && frameTurnEnvironment
                ? viewManager.subscribeFrameRotation(
                      frameTurnEnvironment,
                      countFrameTurn
                  )
                : undefined,
        [viewManager, frameTurnEnvironment]
    );

    // What the last arm resolved: a lifecycle event that resolves the same
    // target leaves the armed tool, and the stroke or line it holds, alone.
    const armedTargetRef = useRef<ArmedTarget | undefined>(undefined);
    const toolManager = viewManager?.interactionContext.toolManager;

    useEffect(() => {
        if (!viewManager) {
            return undefined;
        }
        const { toolManager: tools } = viewManager.interactionContext;
        /**
         * Arms the tool on the target resolved now. `onlyOnChange` skips the
         * arm when the target is the one already armed: a registry lifecycle
         * event about another dataset must not re-create the tool, which
         * would drop a stroke or a line in progress.
         */
        const arm = (onlyOnChange: boolean): void => {
            // Re-resolve per arm; a cached Environment goes stale across
            // release/re-acquire, and lifecycle re-arms run without a render.
            const environment = resolveActiveEnvironment(
                registry,
                structureSet
            );
            // Tools capture the target structure when armed, so an armed
            // brush would keep writing to the active ROI after bulk selection
            // engages, or into an image whose slices are still missing.
            // Resolve no structure while bulk mode is on or the image is
            // incomplete (the write tools then fall back to pan), whatever
            // route selected the tool. A viewing tool takes no structure, so
            // its armed target does not change when the image completes.
            const isImageIncomplete =
                !isWriteImageComplete ||
                environment?.image?.parent.isComplete !== true;
            const structure =
                !takesStructure ||
                isBulkSelectionActive ||
                isImageIncomplete ||
                roiId === null
                    ? undefined
                    : environment?.structureSet?.getRoi(roiId);
            const image = environment?.image;
            // Resolve against the per-arm environment, never the render-time
            // target: a captured structure set goes stale across a re-mint and
            // would clip the stroke to another dataset's ROI.
            const constraints = resolveArmingConstraints(
                constraintsRef.current.boundingRoiConstraint,
                constraintsRef.current.collisionConstraint,
                constraintsRef.current.intensityThresholdConstraint,
                environment?.structureSet,
                environment?.image?.parent
            );
            // The fused pair's Environment is re-resolved per arm for the
            // same reason as the edit target's.
            const fusedEnvironment =
                fusedSeriesUid !== undefined
                    ? registry.environmentFor(fusedSeriesUid)
                    : undefined;
            const target: ArmedTarget = {
                environment,
                structure,
                image,
                fusedEnvironment,
                registration: fusedEnvironment?.registration,
                registrationApproved:
                    fusedEnvironment?.registration?.isApproved,
                secondaryImage: fusedEnvironment?.secondaryImage,
            };
            if (
                onlyOnChange &&
                armedTargetRef.current !== undefined &&
                isSameArmedTarget(armedTargetRef.current, target)
            ) {
                return;
            }
            armedTargetRef.current = target;

            // Cross-section contours belong to the armed cross-section tool
            // and nothing else: every arm starts from none drawn, and a
            // cross-section tool reports what it holds as it is created.
            setCrossSectionDrawingHistory(NO_CROSS_SECTIONS_DRAWN);

            const config = resolveGridToolConfig({
                tool: selectedTool,
                environment,
                structure,
                image,
                viewManager,
                views: environment
                    ? viewManager.viewsFor(environment)
                    : undefined,
                registrationMode,
                isAutoRegistering,
                fusedEnvironment,
                fusedViews: fusedEnvironment
                    ? viewManager.viewsFor(fusedEnvironment)
                    : undefined,
                siblingSupports:
                    selectedTool === 'transform'
                        ? resolveSiblingSupports(
                              environment,
                              structure,
                              transformAllSupport
                          )
                        : undefined,
                brushOptions: {
                    diameter: brushDiameterRef.current,
                    mode,
                    erase,
                    strokeMode: brushStrokeModeRef.current,
                },
                lineOptions,
                lineDeformOptions: {
                    spreadPercent: lineDeformSpreadRef.current,
                },
                deformOptions: {
                    diameter: deformDiameterRef.current,
                    mode: deformOptions.mode,
                },
                smoothBrushOptions: {
                    diameter: smoothBrushDiameterRef.current,
                    strength: smoothBrushOptions.strength,
                    mode: smoothBrushOptions.mode,
                },
                constraints,
                rectangleSelect: {
                    mode: rectangleSelectMode,
                    onSelected: (range) => setRectangleSelection({ range }),
                },
                measurementMode,
                onMeasurementDraftChanged: setMeasurementDraft,
                onMeasurementCommit: (measurement) =>
                    addCommitted(measurementListState, measurement),
                intensityShape,
                onLineProfileDraftChanged: setLineProfileDraft,
                // The placement just made becomes the selected one.
                onLineProfileCommit: (profile) => {
                    addCommitted(lineProfileListState, profile);
                    setSelectedIntensityAnnotation(profile.id);
                },
                onIntensityRegionDraftChanged: setIntensityRegionDraft,
                onIntensityRegionCommit: (region) => {
                    addCommitted(intensityRegionListState, region);
                    setSelectedIntensityAnnotation(region.id);
                },
                onBrushSizeChanged: (diameter) =>
                    setBrushOptions((options) => ({ ...options, diameter })),
                onSpreadChanged: (spreadPercent) =>
                    setLineDeformOptions((options) => ({
                        ...options,
                        spreadPercent,
                    })),
                onDiameterChanged: (diameter) =>
                    setDeformOptions((options) => ({ ...options, diameter })),
                onSmoothBrushDiameterChanged: (diameter) =>
                    setSmoothBrushOptions((options) => ({
                        ...options,
                        diameter,
                    })),
                onCrossSectionsChanged: (drawn) =>
                    setCrossSectionDrawingHistory({
                        canUndo: drawn.canUndo,
                        canRedo: drawn.canRedo,
                        hasDrawing: drawn.hasDrawing,
                    }),
                // Every change to the drawn geometry asks for the preview
                // again; the structure itself is written only on Apply.
                onCrossSectionShapeChanged: () =>
                    setCrossSectionShapeRequest((count) => count + 1),
            });
            if (config) {
                tools.setActiveTool(config);
            } else {
                armPan(tools);
            }
        };
        arm(false);
        return registry.subscribeLifecycle(() => arm(true));
    }, [
        registry,
        viewManager,
        structureSet,
        roiId,
        fusedSeriesUid,
        frameTurnCount,
        registrationMode,
        isAutoRegistering,
        isBulkSelectionActive,
        takesStructure,
        isWriteImageComplete,
        selectedTool,
        mode,
        erase,
        lineOptions,
        deformOptions.mode,
        smoothBrushOptions.strength,
        smoothBrushOptions.mode,
        boundingRoiConstraint,
        collisionConstraint,
        intensityThresholdConstraint,
        measurementMode,
        intensityShape,
        setMeasurementDraft,
        addCommitted,
        setLineProfileDraft,
        setIntensityRegionDraft,
        setSelectedIntensityAnnotation,
        transformAllSupport,
        rectangleSelectMode,
        setRectangleSelection,
        setBrushOptions,
        setLineDeformOptions,
        setDeformOptions,
        setSmoothBrushOptions,
        setCrossSectionDrawingHistory,
        setCrossSectionShapeRequest,
    ]);

    // The armed tool reads these when it is armed and a change does not
    // re-arm it, so each value is written to the armed tool of its class.
    // The tool's own wheel resize writes the atom back with the value the
    // tool already holds.
    usePushToArmedTool(
        toolManager,
        BrushTool,
        'diameter',
        brushOptions.diameter
    );
    usePushToArmedTool(
        toolManager,
        BrushTool,
        'strokeMode',
        brushOptions.strokeMode
    );
    usePushToArmedTool(
        toolManager,
        DeformTool,
        'diameter',
        deformOptions.diameter
    );
    usePushToArmedTool(
        toolManager,
        SmoothBrushTool,
        'diameter',
        smoothBrushOptions.diameter
    );
    usePushToArmedTool(
        toolManager,
        LineDeformTool,
        'spreadPercent',
        lineDeformOptions.spreadPercent
    );

    useEffect(
        () => () => {
            if (toolManager) {
                // Pan holds no cross-section contours.
                setCrossSectionDrawingHistory(NO_CROSS_SECTIONS_DRAWN);
                armPan(toolManager);
            }
        },
        [toolManager, setCrossSectionDrawingHistory]
    );
}

/** The resolved objects an armed tool captures, compared by identity. */
interface ArmedTarget {
    environment: Environment | undefined;
    structure: ToolArmingInputs['structure'];
    image: ToolArmingInputs['image'];
    fusedEnvironment: Environment | undefined;
    registration: Environment['registration'] | undefined;
    registrationApproved: boolean | undefined;
    secondaryImage: Environment['secondaryImage'] | undefined;
}

function isSameArmedTarget(a: ArmedTarget, b: ArmedTarget): boolean {
    return (
        a.environment === b.environment &&
        a.structure === b.structure &&
        a.image === b.image &&
        a.fusedEnvironment === b.fusedEnvironment &&
        a.registration === b.registration &&
        a.registrationApproved === b.registrationApproved &&
        a.secondaryImage === b.secondaryImage
    );
}
